import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import FormTemplateBuilder from '../components/forms/FormTemplateBuilder';
import { useTheme } from '../contexts/ThemeContext';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { useToast } from '../contexts/ToastContext';
import { useTemplateEditor } from '../contexts/TemplateEditorContext';
import { formTemplateService } from '../services/formTemplateService';

export default function EditFormTemplatePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { showToast } = useToast();
  const { currentTemplate, setCurrentTemplate, clearCurrentTemplate } = useTemplateEditor();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplateData = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const templateId = parseInt(id, 10);
      if (isNaN(templateId)) {
        setError('Invalid template ID');
        return;
      }

      console.log(`Fetching template ${templateId} from API...`);
      const template = await formTemplateService.getFormTemplate(templateId);
      console.log('Successfully fetched template from API:', template);

      // Transform the API response to match our context interface
      // The formTemplateService.getFormTemplate() returns schema in { fields: [] } format
      let schemaFields = [];
      if (Array.isArray(template.schema)) {
        schemaFields = template.schema;
      } else if (
        template.schema &&
        typeof template.schema === 'object' &&
        'fields' in template.schema &&
        Array.isArray(template.schema.fields)
      ) {
        schemaFields = template.schema.fields;
      }

      const contextTemplate = {
        id: template.id,
        name: template.name,
        description: template.description || '',
        schema: schemaFields, // Store as array for consistency
        created_at: template.created_at,
        updated_at: template.updated_at,
        version: template.version ?? 1, // Default version since API doesn't return it
        is_latest: template.is_latest ?? true, // Assume it's the latest
        parent_template_id: template.parent_template_id
      };

      setCurrentTemplate(contextTemplate);
      console.log('Template data set in context successfully');
    } catch (err) {
      console.error('Error fetching template:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Failed to load template: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  }, [id, setCurrentTemplate]);

  useEffect(() => {
    if (!id) {
      navigate('/settings/templates');
      return;
    }

    // Prioritize context data - only fetch if context is truly empty or ID mismatch
    if (currentTemplate) {
      const currentId = parseInt(id, 10);

      // Check if context template ID matches URL parameter
      if (currentTemplate.id === currentId) {
        console.log('Using template data from context, skipping API call');
        return;
      }

      // ID mismatch, clear context and fetch correct template
      console.log(
        `ID mismatch: context has ${currentTemplate.id}, URL has ${currentId}. Fetching correct template.`
      );
      clearCurrentTemplate();
    }

    // Context is empty or ID mismatched, need to fetch from API
    console.log('No matching context data available, fetching from API');
    fetchTemplateData();
  }, [id, navigate, currentTemplate, fetchTemplateData, clearCurrentTemplate]);

  const handleSave = async (updatedTemplate: unknown) => {
    if (!currentTemplate) return;

    try {
      const isValidObject = typeof updatedTemplate === 'object' && updatedTemplate !== null;
      const templateData = isValidObject ? updatedTemplate : {};

      const requestBody = {
        ...templateData,
        id: currentTemplate.id,
        create_new_version: true
      };

      const response = await fetch(`/api/form-templates/${currentTemplate.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Failed to update template');
      }

      showToast('Template updated successfully', 'success');
      clearCurrentTemplate();
      navigate('/settings/templates');
    } catch (err) {
      console.error('Error updating template:', err);
      showToast('Failed to update template', 'error');
    }
  };

  const handleCancel = () => {
    clearCurrentTemplate();
    navigate('/settings/templates');
  };

  // Only show loading when we're actively fetching AND don't have context data
  if (loading && !currentTemplate) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div
          className={`animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 ${
            theme === 'dark' ? 'border-indigo-400' : 'border-indigo-600'
          }`}
          aria-label="Loading Template"
        ></div>
      </div>
    );
  }

  if (error || !currentTemplate) {
    const errorMessage = error || (loading ? 'Loading template...' : 'Template not found');
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className={`text-center ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
          <p className="text-lg mb-4">{errorMessage}</p>
          <button
            onClick={handleCancel}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${
              theme === 'dark'
                ? 'text-gray-300 hover:text-gray-200 bg-gray-700 hover:bg-gray-600 border-gray-600'
                : 'text-gray-700 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 border-gray-300'
            }`}
          >
            <ChevronLeftIcon className="h-4 w-4" />
            Back to Templates
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div
        className={`px-0 py-4 border-b mb-6 ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleCancel}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md ${
                theme === 'dark'
                  ? 'text-gray-300 hover:text-gray-200 hover:bg-gray-700'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <ChevronLeftIcon className="h-4 w-4" />
              Back to Templates
            </button>
            <div>
              <h1
                className={`text-xl font-semibold ${
                  theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                }`}
              >
                Edit Template: {currentTemplate.name}
              </h1>
              <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Version {currentTemplate.version} • Last updated:{' '}
                {new Date(currentTemplate.updated_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div>
        <FormTemplateBuilder
          initialTemplate={{
            ...currentTemplate,
            schema: Array.isArray(currentTemplate.schema) ? currentTemplate.schema : []
          }}
          onSave={handleSave}
          editMode={true}
        />
      </div>
    </div>
  );
}
